import Lottie from 'lottie-react';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import animationData from '../../../assets/json/loader.json';
import { getUsersLandingUrl } from '../../../config';
import { useAuthGuard } from '../../../hooks/useAuthGuard';
import { useAuthSystem } from '../../../hooks/useAuthSystem';
import useLogOut from '../../../hooks/useLogOut';
import { useUserLanguage } from '../../../hooks/useUserLanguage';
import { rememberAccessToken } from '../../../utils/agentsRequest';
import UserRestricted from '../Errors/ResrtrictedAccess';

interface AuthWrapperProps {
    children: JSX.Element;
}

const AuthWrapper = ({ children }: AuthWrapperProps): JSX.Element => {
    useAuthGuard();
    const { useLogOutMutation } = useLogOut();
    const { pathname } = useLocation();
    const isEmbedded = useRef(pathname.includes('/embedded'));
    const [isRefreshing, setIsRefreshing] = useState(false);

    const { isLoading, error, user, didMount, checkSession, refetchUserInfo, hasAccessToMindy } = useAuthSystem({
        version:
            globalThis.location.href.includes('platform.quadminds.com') ||
            globalThis.location.href.includes('testing.quadminds.com') ||
            globalThis.location.href.includes('local.quadminds.com')
                ? 1
                : 2,
        redirectUrl: `${getUsersLandingUrl()}/login`,
        debug: process.env['NODE_ENV'] === 'development',
        isEmbedded: isEmbedded.current,
    });

    useUserLanguage(user?.locale);

    const refreshSession = async () => {
        if (isEmbedded.current) {
            setIsRefreshing(true);
            await useLogOutMutation.mutateAsync();
            const token = await checkSession();
            rememberAccessToken(token.accessToken);
            if (token?.refreshToken) {
                localStorage.setItem('refreshToken', token.refreshToken);
            }
            await refetchUserInfo();
            setIsRefreshing(false);
        }
    };
    useEffect(() => {
        refreshSession();
    }, []);

    if (isLoading || isRefreshing) {
        return (
            <div className="flex items-center justify-center h-screen overflow-hidden">
                <div className="h-16 w-36 -mt-12">
                    <Lottie animationData={animationData} loop={true} />
                </div>
            </div>
        );
    }

    if (!hasAccessToMindy && didMount) {
        return <UserRestricted />;
    }

    if (error && didMount && isEmbedded.current) {
        refetchUserInfo();
        return <Navigate to={'/embedded'} />;
    }

    if (error && didMount && !isEmbedded.current) {
        return pathname !== '/error-out-scope/user-not-found' ? (
            <Navigate to="/error-out-scope/user-not-found" />
        ) : (
            children
        );
    }

    return children;
};

export default React.memo(AuthWrapper);
